import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  IconButton,
  Input,
  Switch,
  Text,
  Textarea,
  VStack
} from '@chakra-ui/react'

import { FormField } from '../../../components/FormField'
import { Icon } from '../../../components/Icon'
import { isRichTextEmpty, RichTextField } from '../../../components/RichTextField'
import { FormSelect } from '../../../components/Select'
import { ART_FIELD_ICON_OPTIONS, IArtField, IArtFieldType } from '../../../services/Contests/interfaces'

interface IArtFieldsEditorProps {
  fields: IArtField[]
  notice: string | null
  onChange: (fields: IArtField[]) => void
  onNoticeChange: (notice: string | null) => void
  disabled?: boolean
  /** API validation errors keyed Laravel-style (e.g. "art_fields.0.label"). */
  errors?: Record<string, string[]>
  /** Keys of fields that cannot be removed (contest already has artworks). */
  lockedKeys?: string[]
}

export const FIELD_TYPE_LABELS: Record<IArtFieldType, string> = {
  text: 'Texto curto',
  textarea: 'Texto longo',
  number: 'Número',
  select: 'Lista suspensa (Select)',
  radio: 'Múltipla escolha (Radio)',
  checkbox: 'Caixa de seleção (Checkbox)'
}

const TYPES_WITH_OPTIONS: IArtFieldType[] = ['select', 'radio']

// Only text-like inputs support an example/placeholder hint.
const TYPES_WITH_PLACEHOLDER: IArtFieldType[] = ['text', 'textarea', 'number']

/**
 * Editor for the customizable artwork data questions of a contest (ABRA-73).
 * Standard fields (name/technique/dimensions) can be relabeled and retyped but
 * not removed; custom fields can be freely added and removed.
 */
export default function ArtFieldsEditor({
  fields,
  notice,
  onChange,
  onNoticeChange,
  disabled,
  errors = {},
  lockedKeys = []
}: IArtFieldsEditorProps) {
  // Current selection counts per display target — used to lock further selection at the cap.
  const listingCount = fields.filter((f) => f.show_in_listing).length
  const identifierCount = fields.filter((f) => f.show_in_identifier).length
  const labelsCount = fields.filter((f) => f.show_in_labels).length

  const fieldError = (key: string): string | undefined => errors[key]?.[0]

  // O rótulo e a chave do campo são o mesmo par na tela: a chave é derivada do rótulo, e os
  // dois erros saem juntos, uma linha cada, pelo `error` do `<FormField>`.
  const fieldErrors = (index: number): string[] =>
    [fieldError(`art_fields.${index}.label`), fieldError(`art_fields.${index}.key`)].filter(Boolean) as string[]

  const updateField = (index: number, patch: Partial<IArtField>) => {
    onChange(fields.map((field, i) => (i === index ? { ...field, ...patch } : field)))
  }

  const removeField = (index: number) => {
    onChange(fields.filter((_, i) => i !== index))
  }

  // Reorder a question by swapping it with its neighbour. The array order is the
  // display order everywhere (form, panel, PDFs, e-mails), so this is all it takes.
  const moveField = (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= fields.length) return
    const next = [...fields]
    ;[next[index], next[target]] = [next[target], next[index]]
    onChange(next)
  }

  const addField = () => {
    const newField: IArtField = {
      key: `field_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
      label: '',
      type: 'text',
      icon: '',
      placeholder: '',
      options: [],
      required: false,
      standard: false,
      show_in_listing: false,
      show_in_identifier: false,
      show_in_labels: false
    }
    onChange([...fields, newField])
  }

  const handleTypeChange = (index: number, type: IArtFieldType) => {
    const field = fields[index]
    const patch: Partial<IArtField> = { type }
    // Seed an empty option when switching to a choice type so the user has a starting point
    if (TYPES_WITH_OPTIONS.includes(type) && field.options.length === 0) {
      patch.options = ['']
    }
    updateField(index, patch)
  }

  const updateOption = (fieldIndex: number, optionIndex: number, value: string) => {
    const field = fields[fieldIndex]
    const options = field.options.map((o, i) => (i === optionIndex ? value : o))
    updateField(fieldIndex, { options })
  }

  const addOption = (fieldIndex: number) => {
    const field = fields[fieldIndex]
    updateField(fieldIndex, { options: [...field.options, ''] })
  }

  const removeOption = (fieldIndex: number, optionIndex: number) => {
    const field = fields[fieldIndex]
    updateField(fieldIndex, { options: field.options.filter((_, i) => i !== optionIndex) })
  }

  // Up/down reorder arrows, shown beside the question/label field. Shared by the
  // checkbox (textarea) and non-checkbox (input) label rows.
  const renderMoveControls = (index: number) => (
    <Flex align="center" gap={0} flexShrink={0}>
      <IconButton
        aria-label="Mover pergunta para cima"
        icon={<ChevronUpIcon boxSize={5} />}
        size="xs"
        variant="ghost"
        onClick={() => moveField(index, -1)}
        isDisabled={disabled || index === 0}
      />
      <IconButton
        aria-label="Mover pergunta para baixo"
        icon={<ChevronDownIcon boxSize={5} />}
        size="xs"
        variant="ghost"
        onClick={() => moveField(index, 1)}
        isDisabled={disabled || index === fields.length - 1}
      />
    </Flex>
  )

  return (
    <Box>
      <FormField label="Campos de dados das obras" error={errors['art_fields']} mb={1}>
        <Text variant="caption" mb={3}>
          Personalize as perguntas que o participante responde ao enviar cada obra. Use as setas para alterar a ordem em
          que elas aparecem. O título da obra, o envio da imagem e o leilão são campos fixos e sempre exibidos — não
          precisam ser configurados aqui.
        </Text>
      </FormField>

      {lockedKeys.length > 0 && (
        <Alert status="warning" rounded="lg" mb={3}>
          <AlertIcon />
          Este concurso já possui obras inscritas: os campos existentes não podem ser removidos.
        </Alert>
      )}

      <VStack spacing={3} align="stretch">
        {fields.map((field, index) => (
          <Box key={field.key} border="1px solid" borderColor="gray.200" borderRadius="md" p={3} bg="gray.50">
            {/* Checkboxes are usually consent/declaration statements that can run for a full
                sentence or paragraph, so the label gets its own full-width row (a long input
                cramped into a single column looked broken). */}
            {field.type === 'checkbox' && (
              <Box mb={3}>
                <FormField
                  label={
                    <>
                      Pergunta / Rótulo
                      {field.standard && (
                        <Badge ml={2} colorScheme="purple">
                          Padrão
                        </Badge>
                      )}
                    </>
                  }
                  helperText="Este é o texto que aparece ao lado da caixa de seleção no formulário de envio."
                  error={fieldErrors(index)}
                >
                  <HStack spacing={2} align="flex-start">
                    <Textarea
                      flex="1"
                      size="sm"
                      bg="white"
                      value={field.label}
                      placeholder="Ex: Declaro que esta obra é de minha autoria e está livre de direitos de terceiros."
                      onChange={(e) => updateField(index, { label: e.target.value })}
                      disabled={disabled}
                      maxLength={1000}
                      rows={2}
                      resize="vertical"
                    />
                    {renderMoveControls(index)}
                  </HStack>
                </FormField>
              </Box>
            )}

            {/* The question/label always gets its own full-width top row so it has
                room to breathe; the remaining controls sit on the row below. */}
            {field.type !== 'checkbox' && (
              <Box mb={3}>
                <FormField
                  label={
                    <>
                      Pergunta / Rótulo
                      {field.standard && (
                        <Badge ml={2} colorScheme="purple">
                          Padrão
                        </Badge>
                      )}
                    </>
                  }
                  error={fieldErrors(index)}
                >
                  <HStack spacing={2} align="center">
                    <Input
                      flex="1"
                      size="sm"
                      bg="white"
                      value={field.label}
                      placeholder="Ex: Técnica e suporte"
                      onChange={(e) => updateField(index, { label: e.target.value })}
                      disabled={disabled}
                      maxLength={120}
                    />
                    {renderMoveControls(index)}
                  </HStack>
                </FormField>
              </Box>
            )}

            <Flex gap={3} direction={{ base: 'column', md: 'row' }} align={{ base: 'stretch', md: 'flex-end' }}>
              <Box flex="1">
                <FormField label="Tipo do campo" mb={1} error={fieldError(`art_fields.${index}.type`)}>
                  <FormSelect
                    size="sm"
                    bg="white"
                    isDisabled={disabled}
                    value={field.type}
                    onChange={(value) => handleTypeChange(index, value as IArtFieldType)}
                    options={(Object.keys(FIELD_TYPE_LABELS) as IArtFieldType[]).map((type) => ({
                      value: type,
                      label: FIELD_TYPE_LABELS[type]
                    }))}
                    hideEmptyOption
                  />
                </FormField>
              </Box>

              {/* Checkbox fields render no icon on the public form, so the picker is omitted. */}
              {field.type !== 'checkbox' && (
                <Box flex="1">
                  <FormField label="Ícone" mb={1}>
                    <HStack spacing={2}>
                      <Icon
                        name={field.icon || 'lni lni-text-format'}
                        color="magenta.500"
                        size="lg"
                        w="20px"
                        textAlign="center"
                      />
                      <FormSelect
                        size="sm"
                        bg="white"
                        isDisabled={disabled}
                        value={field.icon ?? ''}
                        onChange={(value) => updateField(index, { icon: value })}
                        options={ART_FIELD_ICON_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
                        placeholder="Padrão (automático)"
                      />
                    </HStack>
                  </FormField>
                </Box>
              )}

              <Flex align="center" gap={2} pb={1}>
                <FormField label="Obrigatório" htmlFor={`required-${field.key}`} isRequired mb={0}>
                  <Switch
                    id={`required-${field.key}`}
                    isChecked={field.required}
                    // The artwork title must always be (it identifies the work)
                    isDisabled={disabled || field.key === 'name'}
                    onChange={(e) => updateField(index, { required: e.target.checked })}
                  />
                </FormField>
              </Flex>

              {field.key !== 'name' && (
                <IconButton
                  aria-label="Remover campo"
                  icon={<Icon name="lni lni-close" />}
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => removeField(index)}
                  isDisabled={disabled || lockedKeys.includes(field.key)}
                />
              )}
            </Flex>

            {field.key !== 'name' && (
              <Flex
                mt={3}
                pl={{ base: 0, md: 2 }}
                gap={{ base: 1, md: 5 }}
                wrap="wrap"
                direction={{ base: 'column', sm: 'row' }}
              >
                <Checkbox
                  isChecked={!!field.show_in_listing}
                  isDisabled={disabled || (!field.show_in_listing && listingCount >= 2)}
                  onChange={(e) => updateField(index, { show_in_listing: e.target.checked })}
                >
                  <Text as="span" fontSize="xs">
                    Exibir na listagem de obras{' '}
                    <Text as="span" color="gray.400">
                      (máx. 2)
                    </Text>
                  </Text>
                </Checkbox>
                <Checkbox
                  isChecked={!!field.show_in_identifier}
                  isDisabled={disabled || (!field.show_in_identifier && identifierCount >= 4)}
                  onChange={(e) => updateField(index, { show_in_identifier: e.target.checked })}
                >
                  <Text as="span" fontSize="xs">
                    Exibir no identificador da obra{' '}
                    <Text as="span" color="gray.400">
                      (máx. 4)
                    </Text>
                  </Text>
                </Checkbox>
                <Checkbox
                  isChecked={!!field.show_in_labels}
                  isDisabled={disabled || (!field.show_in_labels && labelsCount >= 2)}
                  onChange={(e) => updateField(index, { show_in_labels: e.target.checked })}
                >
                  <Text as="span" fontSize="xs">
                    Exibir nas etiquetas das obras{' '}
                    <Text as="span" color="gray.400">
                      (máx. 2)
                    </Text>
                  </Text>
                </Checkbox>
              </Flex>
            )}

            {TYPES_WITH_PLACEHOLDER.includes(field.type) && (
              <Box mt={3} pl={{ base: 0, md: 2 }}>
                <FormField label="Texto de exemplo" mb={1}>
                  <Input
                    size="sm"
                    bg="white"
                    value={field.placeholder ?? ''}
                    placeholder="Ex: Sonho de Verão"
                    onChange={(e) => updateField(index, { placeholder: e.target.value })}
                    disabled={disabled}
                    maxLength={255}
                  />
                </FormField>
                <Text variant="caption" mt={1}>
                  Dica exibida em cinza claro dentro do campo, antes da pessoa responder.
                </Text>
              </Box>
            )}

            {TYPES_WITH_OPTIONS.includes(field.type) && (
              <Box mt={3} pl={{ base: 0, md: 2 }}>
                <FormField label="Opções" mb={1} error={fieldError(`art_fields.${index}.options`)}>
                  <VStack spacing={2} align="stretch">
                    {field.options.map((option, optionIndex) => (
                      <FormField key={optionIndex} error={fieldError(`art_fields.${index}.options.${optionIndex}`)}>
                        <HStack spacing={2}>
                          <Input
                            size="sm"
                            bg="white"
                            value={option}
                            placeholder={`Opção ${optionIndex + 1}`}
                            onChange={(e) => updateOption(index, optionIndex, e.target.value)}
                            disabled={disabled}
                            maxLength={120}
                          />
                          <IconButton
                            aria-label="Remover opção"
                            icon={<Icon name="lni lni-close" />}
                            size="xs"
                            variant="ghost"
                            onClick={() => removeOption(index, optionIndex)}
                            isDisabled={disabled || field.options.length <= 1}
                          />
                        </HStack>
                      </FormField>
                    ))}
                    <Button
                      size="xs"
                      variant="outline"
                      colorScheme="green"
                      onClick={() => addOption(index)}
                      alignSelf="flex-start"
                    >
                      Adicionar opção
                    </Button>
                  </VStack>
                </FormField>
              </Box>
            )}
          </Box>
        ))}
      </VStack>

      <Button mt={3} size="sm" colorScheme="magenta" variant="outline" onClick={addField} isDisabled={disabled}>
        + Adicionar campo
      </Button>

      <Box mt={5}>
        {/* `comment` e não `full`: o aviso é uma linha discreta no formulário público, não o
            regulamento. O Quill converte "- "/"* " no começo da linha em lista sozinho. */}
        <RichTextField
          label="Aviso exibido no formulário (opcional)"
          error={errors['art_fields_notice']}
          mb={1}
          toolbar="comment"
          minH="110px"
          value={notice ?? ''}
          onChange={(content) => onNoticeChange(isRichTextEmpty(content) ? null : content)}
          placeholder="Ex: Este concurso não aceita imagens feitas por IA."
          isReadOnly={disabled}
        />
        <Text variant="caption" mt={1}>
          Aparece de forma discreta no formulário de envio de obras no site público.
        </Text>
      </Box>
    </Box>
  )
}
